import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ConfiguratorState } from '../../types';
import PerspectiveViewer, { ViewName } from '../UI/PerspectiveViewer';
import { formatDisplayPrice } from '../../utils/priceFormat';
import {
  CatalogProduct,
  catalogPreviewDimensions,
  catalogProductKind,
  catalogRangeLabel,
  catalogShortDescription,
  filterCatalogProducts,
  stateUpdatesForProduct,
} from '../../utils/catalogProducts';

/**
 * ============================================================================
 * ProductSelection — Modern rebuild (2×2 Karten-Grid).
 * ---------------------------------------------------------------------------
 * Anforderungen:
 *   – Einfach, modern, keine Editorial-Anmutung.
 *   – Zeichnungen bleiben Herzstück, mit umschaltbarer Perspektive
 *     (Draufsicht / Einbau / Iso) pro Karte.
 *   – „ab X €" bleibt auf der Karte; der laufende Preis wandert in die
 *     sticky Preisleiste (siehe PriceFooterBar).
 * ============================================================================
 */

interface KonfiguratorConfig {
  products: string;
  showVisualizer: boolean;
  theme: string;
  compact: boolean;
}

interface ProductSelectionProps {
  state: ConfiguratorState;
  updateState: (updates: Partial<ConfiguratorState>) => void;
  onNext: () => void;
  onBack?: () => void;
  config?: KonfiguratorConfig;
  onSelectionMade?: () => void;
  showGross?: boolean;
}

type Product = CatalogProduct;

export default function ProductSelection({
  state,
  updateState,
  config,
  onSelectionMade,
  showGross = true,
}: ProductSelectionProps) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewByProduct, setViewByProduct] = useState<Record<string, ViewName>>({});
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/wp-json/skyvision/v1/products');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Ungültige API-Antwort');
      setProducts(data);
    } catch (err) {
      console.error('Fehler beim Laden der Produkte:', err);
      setError('Produkte konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleProductSelect = (product: Product) => {
    updateState(stateUpdatesForProduct(product));
    onSelectionMade?.();
  };

  const availableProducts = filterCatalogProducts(products, config?.products);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-[13px] text-ink-3">Produkte werden geladen …</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 max-w-lg mx-auto">
        <p className="text-[13px] font-medium text-rubric mb-3">Verbindungsfehler</p>
        <p className="text-ink">{error}</p>
        <button type="button" onClick={loadProducts} className="sv-btn sv-btn-ghost mt-6">
          Erneut laden
        </button>
      </div>
    );
  }

  if (availableProducts.length === 0) {
    return (
      <div className="py-16 max-w-lg mx-auto">
        <p className="text-[13px] text-ink-3 mb-3">Keine Produkte verfügbar</p>
        <p className="text-ink">
          Für diese Seite sind aktuell keine Produkte verfügbar. Bitte
          kontaktieren Sie uns direkt — wir helfen persönlich weiter.
        </p>
      </div>
    );
  }

  return (
    <section aria-label="Produktauswahl">
      <header className="mb-8 md:mb-10 max-w-2xl">
        <h2 className="sv-h2 text-ink">
          Welches Flachdachfenster passt zu Ihrer Öffnung?
        </h2>
        <p className="sv-lede mt-3">
          Wählen Sie ein Modell. Bild, Ansicht von oben, Einbau im Dach und
          Schrägansicht zeigen dasselbe Fenster.
        </p>
      </header>

      <div
        role="radiogroup"
        aria-label="Produkt auswählen"
        className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6"
      >
        {availableProducts.map((product, idx) => {
          const isSelected = state.product === product.name;
          const kind = catalogProductKind(product);
          const startingPrice =
            typeof product.startingPrice === 'number' && product.startingPrice > 0
              ? product.startingPrice
              : undefined;
          const currentView = viewByProduct[product.id] ?? 'photo';
          const dims = catalogPreviewDimensions(product);

          return (
            <div
              key={product.id}
              ref={(el) => {
                cardRefs.current[idx] = el;
              }}
              role="radio"
              aria-checked={isSelected}
              tabIndex={(() => {
                const selectedIdx = availableProducts.findIndex((p) => p.name === state.product);
                const tabbableIdx = selectedIdx >= 0 ? selectedIdx : 0;
                return idx === tabbableIdx ? 0 : -1;
              })()}
              onClick={() => handleProductSelect(product)}
              onKeyDown={(e) => {
                const total = availableProducts.length;
                let nextIdx = idx;
                switch (e.key) {
                  case ' ':
                  case 'Enter':
                    e.preventDefault();
                    handleProductSelect(product);
                    return;
                  case 'ArrowDown':
                  case 'ArrowRight':
                    e.preventDefault();
                    nextIdx = (idx + 1) % total;
                    break;
                  case 'ArrowUp':
                  case 'ArrowLeft':
                    e.preventDefault();
                    nextIdx = (idx - 1 + total) % total;
                    break;
                  case 'Home':
                    e.preventDefault();
                    nextIdx = 0;
                    break;
                  case 'End':
                    e.preventDefault();
                    nextIdx = total - 1;
                    break;
                  default:
                    return;
                }
                cardRefs.current[nextIdx]?.focus();
                handleProductSelect(availableProducts[nextIdx]);
              }}
              className={`sv-product-card ${isSelected ? 'is-selected' : ''}`}
            >
              {/* Kopfzeile: Titel + Preis */}
              <div className="sv-product-card-head">
                <div className="min-w-0">
                  <h3 className="sv-product-card-title">{product.name}</h3>
                  <p className="sv-product-card-sub">{catalogShortDescription(product)}</p>
                </div>
                <div className="sv-product-card-price">
                  {startingPrice !== undefined ? (
                    <>
                      <span className="sv-product-card-price-prefix">ab</span>
                      <span className="sv-product-card-price-value">
                        {formatDisplayPrice(startingPrice, showGross, 0)}
                      </span>
                    </>
                  ) : (
                    <span className="sv-product-card-price-onreq">
                      Jetzt Preis
                      <br />
                      konfigurieren
                    </span>
                  )}
                </div>
              </div>

              {/* Zeichnungsfläche mit umschaltbaren Ansichten */}
              <div className="sv-product-card-stage">
                <PerspectiveViewer
                  kind={kind}
                  productKey={product.id}
                  dimensions={dims}
                  selected={isSelected}
                  view={currentView}
                  photoUrl={product.imageUrl}
                  onViewChange={(v) =>
                    setViewByProduct((prev) => ({ ...prev, [product.id]: v }))
                  }
                />
              </div>

              {/* Fußzeile: Maß-Range + Status */}
              <div className="sv-product-card-foot">
                <span className="sv-product-card-range">Bestellmaß {catalogRangeLabel(product)}</span>
                {isSelected ? (
                  <span className="sv-product-card-badge">Ausgewählt</span>
                ) : (
                  <span className="sv-product-card-badge is-muted">Auswählen</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
